import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { SmartLink } from "@features/smart-link";

import { type DocumentSourceType, mapDocumentSource } from "@entities/document";

import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Checkbox } from "@shared/ui/checkbox";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Body, Heading } from "@shared/ui/typography";
import { mod } from "@shared/utils";

import s from "./document-form.module.scss";

type FeedbackFormProps = {
  isOpen?: boolean;
  onSuccess?: () => void;
  bgColor?: "milk" | "white" | "transparent";
  title?: string;
  description?: string;
  text?: string | null;
  className?: string;
  document?: DocumentSourceType | null;
  personal?: DocumentSourceType | null;
  documentFile?: StrapiMedia | null;
};

const feedbackSchema = z.object({
  fullName: z.string().trim().min(1, "Укажите имя и фамилию"),
  email: z
    .email({
      message: "Неправильный формат почты",
    })
    .min(1, "Укажите почту"),
  consent: z
    .boolean()
    .refine((val) => val === true, "Нужно согласиться на обработку данных"),
  newsletterConsent: z
    .boolean()
    .refine((val) => val === true, "Нужно согласиться на получение рассылки"),
});
type FeedbackFormValues = z.infer<typeof feedbackSchema>;
type FeedbackFormInput = z.input<typeof feedbackSchema>;

type SendStatus = "idle" | "sending" | "success" | "error";

const DocumentForm = (props: FeedbackFormProps) => {
  const {
    onSuccess,
    isOpen,
    bgColor = "white",
    title,
    description,
    text,
    document,
    personal,
    documentFile,
    className,
  } = props;

  const mods = mod(s, {
    bgColor,
  });

  const [status, setStatus] = useState<SendStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FeedbackFormInput, unknown, FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      fullName: "",
      email: "",
      consent: false,
      newsletterConsent: false,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      // reset();
      setStatus("idle");
      setErrorMessage(null);
    }
  }, [isOpen]);

  const onSubmit = async (data: FeedbackFormValues) => {
    setStatus("sending");
    setErrorMessage(null);

    try {
      const res = await fetch("/api/document", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...data,
          documentUrl: documentFile?.url,
          documentName: documentFile?.name,
          pageUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
        }),
      });

      const body = await res.json().catch(() => null);

      if (!res.ok) {
        setStatus("error");
        setErrorMessage(
          body?.message || body?.error || "Не удалось отправить документ",
        );
        return;
      }
      setStatus("success");

      setTimeout(() => {
        reset();
        setStatus("idle");
        onSuccess?.();
      }, 2000);
    } catch (_e) {
      setStatus("error");
      setErrorMessage("Ошибка сети или сервера");
    }
  };

  const buttonText = useMemo(() => {
    switch (status) {
      case "sending":
        return "Отправляем...";
      case "success":
        return "Отправлено!";
      default:
        return "Отправить";
    }
  }, [status]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={clsx(s.root, className, mods)}
      // Валидацию и тексты ошибок ведёт react-hook-form: без noValidate браузер
      // блокирует отправку раньше и показывает свой пузырь вместо наших сообщений
      noValidate
    >
      <div className={s.header}>
        <Heading level={5}>{title}</Heading>
        {(text ?? description) && (
          <Body size="caption-small" className={s.description}>
            {text ?? description}
          </Body>
        )}
      </div>
      <div className={s.formBody}>
        <div className={s.field}>
          <Label
            htmlFor="feedback-full-name"
            className={s.fieldLabel}
            requiredMark
          >
            Имя Фамилия
          </Label>
          <Input
            bgColor={bgColor}
            id="feedback-full-name"
            placeholder="Петя Иванов"
            error={errors.fullName?.message}
            {...register("fullName")}
          />
        </div>

        <div className={s.field}>
          <Label htmlFor="feedback-email" className={s.fieldLabel} requiredMark>
            Почта
          </Label>
          <Input
            id="feedback-email"
            type="email"
            placeholder="example@mail.com"
            bgColor={bgColor}
            error={errors.email?.message}
            {...register("email")}
          />
        </div>
      </div>
      <div className={s.footer}>
        <Checkbox error={errors.consent?.message} {...register("consent")}>
          Я подтверждаю, что ознакомлен(а) с{" "}
          <SmartLink source={mapDocumentSource(document)} openInNewTab>
            Политикой конфиденциальности
          </SmartLink>{" "}
          и даю{" "}
          <SmartLink source={mapDocumentSource(personal)}>
            согласие на обработку персональных данных
          </SmartLink>
        </Checkbox>
        <Checkbox
          error={errors.newsletterConsent?.message}
          {...register("newsletterConsent")}
        >
          Согласен(на) на получение рассылки
        </Checkbox>
        <div className={s.relativeBox}>
          <ButtonWithIcon
            variant="secondary-dark"
            type="submit"
            iconName="plus"
            iconStrokeColor="white"
            iconFillColor="cadmium"
            disabled={status === "sending" || status === "success"}
          >
            {buttonText}
          </ButtonWithIcon>
          {status === "error" && (
            <Body
              size="caption"
              weight="semiBold"
              className={s.statusMessageError}
            >
              {errorMessage || "Ошибка отправки"}
            </Body>
          )}

          {status === "success" && (
            <Body
              size="caption"
              weight="semiBold"
              className={s.statusMessageSuccess}
            >
              Документ отправлен на почту!
            </Body>
          )}
        </div>
      </div>
    </form>
  );
};

DocumentForm.displayName = "DocumentForm";

export { DocumentForm };
